import React, { useState } from "react";
import { FaChevronDown, FaPlus, FaTrash } from "react-icons/fa";
import { createFooterColumn, createFooterLink } from "../../../themes/footerMenu";
import CollapseSection from "./CollapseSection";

const PAGE_LINKS = [
  { value: "/", label: "Home" },
  { value: "/collection", label: "Collections" },
  { value: "/track-order", label: "Track order" },
  { value: "/about", label: "About" },
  { value: "/contact", label: "Contact" },
  { value: "/wishlist", label: "Wishlist" },
  { value: "/cart", label: "Cart" },
];

const MAX_COLUMNS = 4;

const GROUPS = [
  {
    id: "colors",
    title: "Colors",
    text: "Background, headings, and links.",
    keys: ["footerBackground", "footerHeadingColor", "footerTextColor"],
  },
  {
    id: "brand",
    title: "Brand",
    text: "Logo and the short store description.",
    keys: ["showFooterLogo", "footerTagline"],
  },
  {
    id: "blocks",
    title: "Blocks",
    text: "Social icons, contact details, and the newsletter.",
    keys: ["showFooterSocial", "showFooterContact", "showNewsletterInFooter", "footerNewsletterTitle"],
  },
  {
    id: "legal",
    title: "Copyright",
    text: "The line along the bottom of every page.",
    keys: ["footerCopyright"],
  },
];

function moveItem(list, index, direction) {
  const target = index + direction;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  const [row] = next.splice(index, 1);
  next.splice(target, 0, row);
  return next;
}

function pageValue(link) {
  return PAGE_LINKS.some((page) => page.value === link) ? link : "__custom__";
}

function newsletterOn(settings) {
  return settings.showNewsletterInFooter === true;
}

export function FooterSettings({ fields, settings, renderField, menus, onMenus }) {
  const [openId, setOpenId] = useState(null);
  const byKey = Object.fromEntries((fields || []).map((field) => [field.key, field]));
  const columnPreview = (menus || []).map((column) => column.title).filter(Boolean).join(" · ");
  const toggle = (id) => setOpenId((current) => (current === id ? null : id));

  return (
    <div className="te-footer-settings">
      {GROUPS.map((group) => {
        const groupFields = group.keys
          .filter((key) => key !== "footerNewsletterTitle" || newsletterOn(settings))
          .map((key) => byKey[key])
          .filter(Boolean);
        if (groupFields.length === 0) return null;
        return (
          <CollapseSection
            key={group.id}
            title={group.title}
            text={group.text}
            open={openId === group.id}
            onToggle={() => toggle(group.id)}
          >
            {groupFields.map((field) => (
              <React.Fragment key={field.key}>{renderField(field)}</React.Fragment>
            ))}
          </CollapseSection>
        );
      })}
      <CollapseSection
        title="Menu columns"
        text={columnPreview || "Links along the bottom of the store."}
        extra={`${(menus || []).length}/${MAX_COLUMNS}`}
        open={openId === "menus"}
        onToggle={() => toggle("menus")}
      >
        <FooterMenuEditor menus={menus} onChange={onMenus} />
      </CollapseSection>
    </div>
  );
}

export default function FooterMenuEditor({ menus, onChange }) {
  const [openId, setOpenId] = useState(null);

  React.useEffect(() => {
    if (!openId) return;
    if (menus.some((column) => column.id === openId)) return;
    setOpenId(menus[0]?.id || null);
  }, [menus, openId]);

  const updateColumn = (index, column) => {
    const next = [...menus];
    next[index] = column;
    onChange(next);
  };

  const addColumn = () => {
    const column = createFooterColumn({ title: "New menu" });
    onChange([...menus, column]);
    setOpenId(column.id);
  };

  return (
    <div className="te-footer-columns">
      {menus.length === 0 && (
        <div className="te-footer-empty">No menu columns yet. Add one for links such as shipping or contact.</div>
      )}

      {menus.map((column, index) => {
        const items = column.items || [];
        const open = openId === column.id;
        const preview = items.map((item) => item.label).filter(Boolean).join(" · ");
        return (
          <article className={`te-footer-column ${open ? "is-open" : ""}`} key={column.id}>
            <button
              type="button"
              className="te-footer-column-toggle"
              aria-expanded={open}
              onClick={() => setOpenId(open ? null : column.id)}
            >
              <span className="te-footer-index">{index + 1}</span>
              <span className="te-footer-column-summary">
                <strong>{column.title || "Untitled column"}</strong>
                <small>{preview || "No links yet"}</small>
              </span>
              <span className="te-footer-link-count">{items.length}</span>
              <FaChevronDown size={11} className="te-footer-chevron" />
            </button>

            {open && (
              <div className="te-footer-links">
                <div className="te-footer-column-tools">
                  <label className="te-footer-title-field">
                    <span className="te-label">Heading</span>
                    <input
                      className="te-input"
                      value={column.title}
                      placeholder="Column heading"
                      aria-label={`Column ${index + 1} heading`}
                      onChange={(event) => updateColumn(index, { ...column, title: event.target.value })}
                    />
                  </label>
                  <div className="te-footer-tool-row">
                    <button
                      type="button"
                      className="te-footer-text-btn"
                      disabled={index === 0}
                      onClick={() => onChange(moveItem(menus, index, -1))}
                    >
                      Move left
                    </button>
                    <button
                      type="button"
                      className="te-footer-text-btn"
                      disabled={index === menus.length - 1}
                      onClick={() => onChange(moveItem(menus, index, 1))}
                    >
                      Move right
                    </button>
                    <button
                      type="button"
                      className="te-footer-text-btn is-danger"
                      onClick={() => {
                        const next = menus.filter((row) => row.id !== column.id);
                        onChange(next);
                        setOpenId(next[Math.min(index, next.length - 1)]?.id || null);
                      }}
                    >
                      Remove column
                    </button>
                  </div>
                </div>

                {items.length === 0 && <p className="te-footer-link-empty">This column has no links.</p>}

                {items.map((item, itemIndex) => {
                  const selectedPage = pageValue(item.link);
                  return (
                    <div className="te-footer-link" key={item.id}>
                      <input
                        className="te-input"
                        value={item.label}
                        placeholder="Link name"
                        aria-label="Link name"
                        onChange={(event) => {
                          const nextItems = [...items];
                          nextItems[itemIndex] = { ...item, label: event.target.value };
                          updateColumn(index, { ...column, items: nextItems });
                        }}
                      />
                      <select
                        className="te-select"
                        aria-label="Link destination"
                        value={selectedPage}
                        onChange={(event) => {
                          const value = event.target.value;
                          const nextItems = [...items];
                          nextItems[itemIndex] = {
                            ...item,
                            link: value === "__custom__" ? "" : value,
                          };
                          updateColumn(index, { ...column, items: nextItems });
                        }}
                      >
                        {PAGE_LINKS.map((page) => (
                          <option key={page.value} value={page.value}>
                            {page.label}
                          </option>
                        ))}
                        <option value="__custom__">Custom link</option>
                      </select>
                      <div className="te-footer-icon-actions">
                        <button
                          type="button"
                          className="te-footer-text-btn"
                          disabled={itemIndex === 0}
                          onClick={() => updateColumn(index, { ...column, items: moveItem(items, itemIndex, -1) })}
                        >
                          Up
                        </button>
                        <button
                          type="button"
                          className="te-footer-text-btn"
                          disabled={itemIndex === items.length - 1}
                          onClick={() => updateColumn(index, { ...column, items: moveItem(items, itemIndex, 1) })}
                        >
                          Down
                        </button>
                        <button
                          type="button"
                          className="te-mini is-danger"
                          title="Remove link"
                          aria-label={`Remove ${item.label || "link"}`}
                          onClick={() =>
                            updateColumn(index, {
                              ...column,
                              items: items.filter((row) => row.id !== item.id),
                            })
                          }
                        >
                          <FaTrash size={11} />
                        </button>
                      </div>
                      {selectedPage === "__custom__" && (
                        <input
                          className="te-input te-footer-custom"
                          placeholder="https://example.com or /about"
                          aria-label="Custom URL"
                          value={item.link}
                          onChange={(event) => {
                            const nextItems = [...items];
                            nextItems[itemIndex] = { ...item, link: event.target.value };
                            updateColumn(index, { ...column, items: nextItems });
                          }}
                        />
                      )}
                    </div>
                  );
                })}

                <button
                  type="button"
                  className="te-footer-add-link"
                  onClick={() =>
                    updateColumn(index, {
                      ...column,
                      items: [...items, createFooterLink({ label: "New link", link: "/" })],
                    })
                  }
                >
                  <FaPlus size={10} /> Add link
                </button>
              </div>
            )}
          </article>
        );
      })}

      <button type="button" className="te-add" disabled={menus.length >= MAX_COLUMNS} onClick={addColumn}>
        <FaPlus size={11} /> Add menu column
      </button>
    </div>
  );
}
